import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './Timeline.module.scss';
import moment from 'moment-timezone';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useMemberCompanyTimezones } from '@/hooks/useMemberCompanyTimezones';
import { useMembersOptionsForSelect } from '@/hooks/useMembersOptionsForSelect';
import { timelineActionTypes } from '@/store/Timeline/Timeline.actionTypes';
import memberSessionsService from '@/services/memberSessions';
import { timelineTableConfig } from './Timeline.config';
import { Spinner } from 'reactstrap';
import { TimelineScreenshotsLightbox } from './TimelineScreenshotsLightbox.component';
import { DayTimelineCardComponent as DayTimelineCard } from './components/DayTimelineCard.component';
import { ReportFiltersBar } from '@/components/ReportFiltersBar';
import { getActivityColor } from '@/utils/activityColor';
import { formatPercent } from '@/utils/numberUtils';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import httpClient from '@/config/httpClient';
import { useHistory, useLocation } from 'react-router-dom';
import {
  useUrlDateRangeSync,
  useUrlMemberHydrationSync,
  useUrlMemberNavSync,
  useUrlTimezoneIgnoreFlagSync,
  useUrlFilterWrite,
  getDefaultMemberFromOptions,
} from '@/hooks/useUrlFilterSync';
import {
  getInitialReportDateRangeFromSearch,
  getReportFiltersFromSearch,
} from '@/utils/reportFiltersUrl';
import { EmptyState } from '@/components/EmptyState';
import {
  collectScreenshotsFromActivities,
  getScreenshotRevealKey,
} from '@/utils/screenshotPrivacy';

// Consecutive fetched days with zero sessions before assuming there's no more history.
const EMPTY_DAY_STREAK_LIMIT = 10;
// Hard safety cap on how many days of backward pagination a single date can trigger.
const MAX_LOOKBACK_DAYS = 180;

const EXPORT_ENDPOINTS = {
  csv: '/exportTimelineCsv',
  pdf: '/exportTimelinePdf',
};

export const TimelineComponent = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const dispatch = useDispatch();
  const { sessionsByDay, loading, loadingMore, page, pageSize, filters } =
    useSelector((state) => {
      return state.timeline;
    });
  const team = useSelector((state) => state.team);
  const auth = useSelector((state) => state.auth);
  const location = useLocation();
  const history = useHistory();

  const teamId = team?.active?.activeTeamid;
  const companyId = auth?.profile?.activeCompany?.companyId;

  const [selectedMember, setSelectedMember] = useState(null);
  const memberId = selectedMember?.value || null;
  const employeeName = selectedMember?.label?.replace(/\s*\([^)]*\)\s*$/, '');
  const teamName = team?.active?.Team;
  const {
    membersOptions,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useMembersOptionsForSelect(teamId);

  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  const [range, setRange] = useState(() =>
    getInitialReportDateRangeFromSearch(location.search)
  );

  const [ignoreUrlTimezone, setIgnoreUrlTimezone] = useState(false);
  const userOverrideTimezoneRef = useRef(false);

  useUrlDateRangeSync({ location, setDateRange: setRange });
  useUrlMemberHydrationSync({
    location,
    enabled: Boolean(teamId),
    membersOptions,
    selectedMember,
    setSelectedMember,
    currentUserId: auth?.profile?.id,
    getDefaultMember: getDefaultMemberFromOptions,
  });
  useUrlMemberNavSync({
    location,
    enabled: Boolean(teamId),
    membersOptionsRef,
    setSelectedMember,
  });
  useUrlTimezoneIgnoreFlagSync({ history, setIgnoreUrlTimezone });

  const filtersFromUrl = useMemo(
    () => getReportFiltersFromSearch(location.search),
    [location.search]
  );
  const urlTimezoneForHook = useMemo(
    () => (!ignoreUrlTimezone && filtersFromUrl.tz ? filtersFromUrl.tz : null),
    [ignoreUrlTimezone, filtersFromUrl.tz]
  );

  const {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
  } = useMemberCompanyTimezones({
    teamId,
    memberId,
    companyId,
    initialTimezone: urlTimezoneForHook || filters?.timeZone || null,
    autoSelectMemberTimezone: true,
    userOverrideTimezoneRef,
  });

  const writeParams = useMemo(() => {
    const params = { start: range.start, end: range.end };
    if (selectedMember?.value) params.member = selectedMember.value;
    if (selectedTimezone) params.tz = selectedTimezone;
    return params;
  }, [range.start, range.end, selectedMember?.value, selectedTimezone]);

  useUrlFilterWrite({
    history,
    location,
    params: writeParams,
    enabled: Boolean(teamId && range?.start && range?.end),
  });

  const onMemberFilterChange = useCallback(
    (opt) => {
      userOverrideTimezoneRef.current = false;
      setIgnoreUrlTimezone(true);
      setSelectedMember(opt);
    },
    [setSelectedMember]
  );

  const handleTimezoneFilterChange = useCallback(
    (value) => {
      userOverrideTimezoneRef.current = true;
      setIgnoreUrlTimezone(true);
      setSelectedTimezone(value);
    },
    [setSelectedTimezone, setIgnoreUrlTimezone, userOverrideTimezoneRef]
  );
  const [localPage, setLocalPage] = useState(page || 1);
  const PAGE_SIZE = 10;
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState('');
  const [sessionActivities, setSessionActivities] = useState([]);
  const [activeSessionMeta, setActiveSessionMeta] = useState(null);
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(0);
  const modalActivitiesCacheRef = useRef({});
  const [oldestLoadedDate, setOldestLoadedDate] = useState(range.start);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingMoreRef = useRef(false);
  const emptyStreakRef = useRef(0);
  const lookbackDaysRef = useRef(0);
  const wasLoadingMoreRef = useRef(false);

  const getModalActivitiesCacheKey = (
    actualSessionId,
    timezone,
    segStart,
    segEnd
  ) =>
    `${actualSessionId}::${timezone || 'UTC'}::${segStart || ''}::${segEnd || ''}`;

  const getEmployeeIdForUser = (userId) => {
    if (!userId) return '';
    const match = membersOptions.find((m) => m.value === userId);
    return match?.employeeId ?? userId;
  };

  const getRateForUser = (userId) => {
    const match = membersOptions.find((m) => m.value === userId);
    return match?.rate || 0;
  };

  const fetchTimeline = (nextPage = localPage) => {
    if (!selectedMember) return;
    const employeeId = getEmployeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        page: nextPage,
        pageSize: PAGE_SIZE,
        rate: getRateForUser(selectedMember.value),
      },
    });
  };
  useEffect(() => {
    if (selectedMember) {
      modalActivitiesCacheRef.current = {};
      fetchTimeline(1);
      setLocalPage(1);
      // Reset backward-pagination state so scrolling starts fresh from the newly selected date
      setOldestLoadedDate(range.start);
      setHasMore(true);
      isFetchingMoreRef.current = false;
      emptyStreakRef.current = 0;
      lookbackDaysRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember, selectedTimezone, range.start, range.end]);

  // Fetch the previous day's sessions and append them below what's already loaded
  const loadMorePreviousDay = useCallback(() => {
    if (
      isFetchingMoreRef.current ||
      !hasMore ||
      !selectedMember ||
      !teamId ||
      lookbackDaysRef.current >= MAX_LOOKBACK_DAYS
    ) {
      return;
    }

    const nextDate = moment(oldestLoadedDate, 'YYYY-MM-DD')
      .subtract(1, 'day')
      .format('YYYY-MM-DD');
    const employeeId = getEmployeeIdForUser(selectedMember.value);

    isFetchingMoreRef.current = true;
    lookbackDaysRef.current += 1;
    setOldestLoadedDate(nextDate);

    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: nextDate,
        endDate: nextDate,
        timeZone: selectedTimezone,
        mergeMode: 'append',
        rate: getRateForUser(selectedMember.value),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasMore,
    oldestLoadedDate,
    selectedMember,
    teamId,
    companyId,
    selectedTimezone,
  ]);

  useEffect(() => {
    if (wasLoadingMoreRef.current && !loadingMore) {
      const loadedDay = (sessionsByDay || []).find(
        (day) => day.dayKey === oldestLoadedDate
      );
      if (loadedDay?.sessions?.length) {
        emptyStreakRef.current = 0;
      } else {
        emptyStreakRef.current += 1;
      }
      if (
        emptyStreakRef.current >= EMPTY_DAY_STREAK_LIMIT ||
        lookbackDaysRef.current >= MAX_LOOKBACK_DAYS
      ) {
        setHasMore(false);
      }
      isFetchingMoreRef.current = false;
    }
    wasLoadingMoreRef.current = !!loadingMore;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, sessionsByDay, oldestLoadedDate]);

  const closeScreenshotsModal = () => {
    setScreenshotModalOpen(false);

    setActivitiesLoading(false);
    setActivitiesError('');
    setSessionActivities([]);
    setActiveSessionMeta(null);
    setSelectedScreenshotIndex(0);
  };
  // Helper function to filter activities by time range (for split segments)
  const filterActivitiesByTimeRange = (
    activities,
    segmentStart,
    segmentEnd,
    timezone
  ) => {
    if (!segmentStart || !segmentEnd) {
      return activities; // No filtering if no time range provided
    }

    const segmentStartMoment = moment(segmentStart).tz(timezone);
    const segmentEndMoment = moment(segmentEnd).tz(timezone);

    return activities.filter((activity) => {
      if (!activity.timestamp) return false;
      const activityMoment = moment(activity.timestamp).tz(timezone);
      // Include activities that are >= segmentStart and <= segmentEnd
      // This includes screenshots at the exact end time
      return (
        activityMoment.isSameOrAfter(segmentStartMoment) &&
        activityMoment.isSameOrBefore(segmentEndMoment)
      );
    });
  };

  const openScreenshotsModal = async (session, dayLabel) => {
    // Check if this is a split segment
    const isSplitSegment = session?.isSplit && session?.parentSessionId;
    const actualSessionId = isSplitSegment
      ? session.parentSessionId
      : session?.id;

    setActiveSessionMeta({
      sessionId: session?.id,
      dayLabel,
      start: session?.start,
      end: session?.end,
    });
    setActivitiesError('');
    setScreenshotModalOpen(true);

    if (!actualSessionId) {
      setActivitiesError('Session id not available.');
      setSessionActivities([]);
      return;
    }

    if (!teamId || !selectedMember?.value) {
      setActivitiesError('Missing team or user information.');
      setSessionActivities([]);
      return;
    }

    const cacheKey = getModalActivitiesCacheKey(
      actualSessionId,
      selectedTimezone,
      isSplitSegment ? session.start : null,
      isSplitSegment ? session.end : null
    );

    if (modalActivitiesCacheRef.current[cacheKey]) {
      setSessionActivities(modalActivitiesCacheRef.current[cacheKey]);
      setSelectedScreenshotIndex(0);
      setActivitiesLoading(false);
      return;
    }

    setSessionActivities([]);
    setActivitiesLoading(true);
    try {
      const employeeId = getEmployeeIdForUser(selectedMember.value);

      // Find the parent session's start time for path determination
      let sessionStartTime = session.start;
      if (isSplitSegment && sessionsByDay) {
        // Find the parent session or any segment with the same parentSessionId to get original start time
        for (const day of sessionsByDay) {
          const foundSession = day.sessions?.find(
            (s) =>
              s.id === actualSessionId ||
              (s.parentSessionId === actualSessionId && s.start)
          );
          if (foundSession?.start) {
            sessionStartTime = foundSession.start;
            break;
          }
        }
      }

      const result = await memberSessionsService.getSessionActivities({
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        sessionId: actualSessionId, // Use parent session ID for split segments
        sessionStartTime, // Pass session startTime to determine path
      });

      if (result.success) {
        let activities = result.data.activities || [];

        // If this is a split segment, filter activities by the segment's time range
        if (isSplitSegment && session.start && session.end) {
          activities = filterActivitiesByTimeRange(
            activities,
            session.start,
            session.end,
            selectedTimezone
          );
        }

        modalActivitiesCacheRef.current[cacheKey] = activities;
        setSessionActivities(activities);
        setSelectedScreenshotIndex(0);
      } else {
        setActivitiesError('Unable to load screenshots for this session.');
      }
    } catch (error) {
      console.error('Error loading session activities', error);
      setActivitiesError('Unable to load screenshots for this session.');
    } finally {
      setActivitiesLoading(false);
    }
  };

  const flattenedScreens = useMemo(() => {
    // Activities arrive oldest-first; show newest screenshots first.
    return collectScreenshotsFromActivities(sessionActivities)
      .map((row) => ({
        ...row,
        revealKey: getScreenshotRevealKey(row),
      }))
      .reverse();
  }, [sessionActivities]);

  useEffect(() => {
    if (!flattenedScreens.length) {
      setSelectedScreenshotIndex(0);
    } else if (selectedScreenshotIndex >= flattenedScreens.length) {
      setSelectedScreenshotIndex(0);
    }
  }, [flattenedScreens, selectedScreenshotIndex]);

  const onExport = async (format = 'csv') => {
    try {
      await httpClient.post(
        EXPORT_ENDPOINTS[format] || EXPORT_ENDPOINTS.csv,
        {
          loggedInUser: {
            id: auth?.profile?.id,
            email: auth?.profile?.email,
            name: auth?.profile?.name,
          },
          teamName: team?.active?.Team,
          companyName: auth?.profile?.activeCompany?.companyName,
          memberName: selectedMember?.label,
          teamId,
          userUID: selectedMember?.value,
          companyId,
          employeeId: getEmployeeIdForUser(selectedMember?.value),
          memberId: selectedMember?.value,
          startDate: range.start,
          endDate: range.end,
          timeZone: selectedTimezone,
          rate: getRateForUser(selectedMember?.value),
        },
        // The backend now awaits the full job (session fetch, PDF render,
        // upload, email) before responding, so this can legitimately take
        // much longer than httpClient's default 60s on a cold Puppeteer
        // start. Even if this still times out client-side, the export
        // keeps running server-side and the Notifications/toast listener
        // is the real "done" signal, not this response.
        { timeout: 180000 }
      );
    } catch (error) {
      console.error('Error exporting timeline', error);
    } finally {
      setExportLoading(false);
    }
  };
  return (
    <div className={styles.timelinePage}>
      <ReportFiltersBar
        membersOptions={membersOptions}
        selectedMember={selectedMember}
        onMemberFilterChange={onMemberFilterChange}
        membersLoading={membersLoading}
        membersError={membersError}
        membersErrorObj={membersErrorObj}
        refetchMembers={refetchMembers}
        timezoneOptions={timezoneOptions}
        selectedTimezone={selectedTimezone}
        onTimezoneChange={handleTimezoneFilterChange}
        timezonesLoading={timezonesLoading}
        date={range.start}
        onDateChange={(dateStr) => setRange({ start: dateStr, end: dateStr })}
        onRefresh={() => {
          modalActivitiesCacheRef.current = {};
          fetchTimeline(1);
          setLocalPage(1);
        }}
        onExport={(format) => {
          setExportLoading(true);
          onExport(format);
        }}
        exportFormats={['csv', 'pdf']}
        exportLoading={exportLoading}
        exportDisabled={!sessionsByDay?.length}
      />
      {loading && (
        <div className={styles.loaderContainer}>
          <Spinner color="primary" />
        </div>
      )}
      {!loading && !sessionsByDay.length && (
        <EmptyState
          illustration
          title="No Sessions Found"
          description="No sessions found for the selected filters. Try a different date range."
        />
      )}
      {!loading && sessionsByDay.length > 0 && (
        <InfiniteScroll
          dataLength={sessionsByDay.length}
          next={loadMorePreviousDay}
          hasMore={hasMore}
          scrollableTarget="app-scroll-container"
          scrollThreshold={0.9}
          className={styles.dayCardsList}
          style={{ overflow: 'visible', width: '100%' }}
          loader={
            loadingMore && (
              <div className={styles.loaderContainer}>
                <Spinner size="sm" color="primary" />
              </div>
            )
          }
        >
          {sessionsByDay.map((day, index) => {
            const segments = day.sessions.map((session) => {
              const startTime = moment(session.start)
                .tz(selectedTimezone)
                .format('HH:mm');
              const endTime = moment(session.end)
                .tz(selectedTimezone)
                .format('HH:mm');
              const { displayIdle, idlePercent } = normalizeIdleForDisplay(
                session.durationSec,
                session.idleSec
              );
              return {
                start12H: moment(session.start)
                  .tz(selectedTimezone)
                  .format('hh:mm A'),
                end12H: moment(session.end)
                  .tz(selectedTimezone)
                  .format('hh:mm A'),
                totalTimeTracked: formatTimeFromSeconds(session.durationSec),
                idleTime: formatTimeFromSeconds(displayIdle),
                activityPct: formatPercent(session.activityPct),
                idlePercent: formatPercent(idlePercent),
                start24H: startTime,
                end24H: endTime,
                color: getActivityColor(session.activityPct, true),
              };
            });
            return (
              <DayTimelineCard
                key={day.dayKey}
                day={day}
                index={index}
                segments={segments}
                memberId={selectedMember?.value}
                tableColumns={timelineTableConfig({
                  selectedTimezone,
                  openScreenshotsModal,
                  dayLabel: day.dayLabel,
                })}
              />
            );
          })}
        </InfiniteScroll>
      )}
      <TimelineScreenshotsLightbox
        open={screenshotModalOpen}
        onClose={closeScreenshotsModal}
        activitiesLoading={activitiesLoading}
        activitiesError={activitiesError}
        flattenedScreens={flattenedScreens}
        selectedScreenshotIndex={selectedScreenshotIndex}
        selectedTimezone={selectedTimezone}
        setSelectedScreenshotIndex={setSelectedScreenshotIndex}
        activeSessionMeta={activeSessionMeta}
        employeeName={employeeName}
        teamName={teamName}
      />
    </div>
  );
};
